import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2, Eye, EyeOff, Upload, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../ui/dialog";
import { Switch } from "../../ui/switch";
import { mk, money } from "../../../lib/marketApi";
import { apiUploadFile } from "../../../api";
import { inputCls, Labeled } from "./ui";

const EMPTY = { name: "", description: "", price: "", compare_at: "", category: "", images: [], stock: 10, active: true };

const ProductDialog = ({ open, initial, onClose, onSave }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [up, setUp] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    setF(initial ? { ...EMPTY, ...initial } : EMPTY);
    setErr("");
  }, [initial, open]);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const addImages = async (files) => {
    for (const file of [...files]) {
      setUp(0);
      try {
        const { url } = await apiUploadFile(file, setUp);
        setF((x) => ({ ...x, images: [...x.images, url] }));
      } catch {
        setErr("No se pudo subir una imagen");
      }
    }
    setUp(null);
  };
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await onSave({ ...f, price: Number(f.price) || 0, compare_at: Number(f.compare_at) || 0, stock: parseInt(f.stock, 10) || 0 });
      onClose();
    } catch (e2) {
      setErr(e2.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-lg overflow-y-auto rounded-2xl" data-testid="product-dialog">
        <DialogHeader>
          <DialogTitle>{initial?.id ? "Editar producto" : "Nuevo producto"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <Labeled label="Fotos">
            <div className="flex flex-wrap gap-2">
              {f.images.map((u, i) => (
                <div key={u + i} className="relative h-20 w-20 overflow-hidden rounded-xl border">
                  <img src={u} alt="" className="h-full w-full object-cover" />
                  <button type="button" onClick={() => setF((x) => ({ ...x, images: x.images.filter((_, j) => j !== i) }))} className="absolute right-1 top-1 rounded-full bg-black/60 p-0.5 text-white">
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
              <label className="flex h-20 w-20 cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-slate-300 text-xs text-slate-500 hover:border-blue-400" data-testid="product-images-upload">
                {up !== null ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
                {up !== null ? `${up}%` : "Agregar"}
                <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => addImages(e.target.files)} />
              </label>
            </div>
          </Labeled>
          <Labeled label="Nombre"><input required className={inputCls} value={f.name} onChange={set("name")} data-testid="product-name-input" /></Labeled>
          <div className="grid grid-cols-2 gap-3">
            <Labeled label="Precio"><input required type="number" min="0" step="0.01" className={inputCls} value={f.price} onChange={set("price")} data-testid="product-price-input" /></Labeled>
            <Labeled label="Precio antes (opcional)"><input type="number" min="0" step="0.01" className={inputCls} value={f.compare_at || ""} onChange={set("compare_at")} data-testid="product-compare-input" /></Labeled>
            <Labeled label="Categoría"><input className={inputCls} value={f.category} onChange={set("category")} data-testid="product-category-input" /></Labeled>
            <Labeled label="Existencias"><input type="number" min="0" className={inputCls} value={f.stock} onChange={set("stock")} data-testid="product-stock-input" /></Labeled>
          </div>
          <Labeled label="Descripción"><textarea rows={4} className={inputCls} value={f.description} onChange={set("description")} data-testid="product-description-input" /></Labeled>
          <div className="flex items-center justify-between rounded-xl bg-slate-50 px-3.5 py-2.5">
            <span className="text-sm text-slate-700">Visible en la tienda</span>
            <Switch checked={f.active} onCheckedChange={(v) => setF((x) => ({ ...x, active: v }))} data-testid="product-active-switch" />
          </div>
          {err && <p className="text-sm text-red-600">{err}</p>}
          <button disabled={busy || up !== null} className="flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 py-3 text-sm font-semibold text-white disabled:opacity-60" data-testid="product-save-button">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Guardar producto
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const ProductsTab = ({ sid, token, cur }) => {
  const [list, setList] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = () => mk(`/owner/${sid}/products`, { token }).then(setList);
  useEffect(() => {
    load();
  }, [sid]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (p) => {
    if (p.id) await mk(`/owner/${sid}/products/${p.id}`, { method: "PUT", token, body: p });
    else await mk(`/owner/${sid}/products`, { method: "POST", token, body: p });
    load();
  };
  const remove = async (p) => {
    if (!window.confirm(`¿Eliminar "${p.name}"?`)) return;
    await mk(`/owner/${sid}/products/${p.id}`, { method: "DELETE", token });
    load();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">{list ? `${list.length} productos` : "Cargando…"}</p>
        <button onClick={() => setEdit({})} className="inline-flex items-center gap-1.5 rounded-full bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow" data-testid="add-product-button">
          <Plus className="h-4 w-4" /> Producto
        </button>
      </div>
      <div className="divide-y divide-slate-100 overflow-hidden rounded-2xl border border-slate-200 bg-white">
        {(list || []).map((p) => (
          <div key={p.id} className="flex items-center gap-3 p-3" data-testid={`admin-product-${p.id}`}>
            <img src={p.images[0]} alt="" className="h-14 w-14 shrink-0 rounded-xl bg-slate-100 object-cover" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-slate-900">{p.name}</p>
              <p className="text-xs text-slate-500">
                {money(p.price, cur)} · {p.stock} en stock {p.category && `· ${p.category}`}
              </p>
            </div>
            {p.active ? <Eye className="h-4 w-4 text-emerald-500" /> : <EyeOff className="h-4 w-4 text-slate-400" />}
            <button onClick={() => setEdit(p)} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100" data-testid={`edit-product-${p.id}`}><Pencil className="h-4 w-4" /></button>
            <button onClick={() => remove(p)} className="rounded-lg p-2 text-red-500 hover:bg-red-50" data-testid={`delete-product-${p.id}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
      <ProductDialog open={!!edit} initial={edit} onClose={() => setEdit(null)} onSave={save} />
    </div>
  );
};
